// Import direto de `enums` (e não do barril `_metronic/helpers`): o barril
// arrasta o worker do pdfjs, que quebra este módulo em teste unitário.
import { CargoType, Permission } from '../../../../../_metronic/helpers/enums'
import type { IOption } from '../../../../components'
import {
  DiaSemana,
  EscalaAgendaViewEmpresa,
  EscalaAgendaViewPessoa,
  TipoEscala,
  TipoFeriado,
} from './_models'

/* -------------------------------------------------------------------------- */
/* Rótulos                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Dias da semana. **Não há endpoint** `/tipos/dia-semana` no backend — o enum
 * `DiaSemana` é um conjunto fechado e a ordem abaixo é a mesma da classe Java
 * (`SEGUNDA` … `DOMINGO`).
 */
export const DIAS_SEMANA_LABELS: Record<DiaSemana, string> = {
  [DiaSemana.Segunda]: 'Segunda',
  [DiaSemana.Terca]: 'Terça',
  [DiaSemana.Quarta]: 'Quarta',
  [DiaSemana.Quinta]: 'Quinta',
  [DiaSemana.Sexta]: 'Sexta',
  [DiaSemana.Sabado]: 'Sábado',
  [DiaSemana.Domingo]: 'Domingo',
}

export const DIAS_SEMANA_OPTIONS: IOption[] = Object.entries(DIAS_SEMANA_LABELS).map(
  ([value, label]) => ({ value, label })
)

export const TIPO_ESCALA_LABELS: Record<TipoEscala, string> = {
  [TipoEscala.Disponibilidade]: 'Disponibilidade',
  [TipoEscala.Indisponibilidade]: 'Indisponibilidade',
}

export const TIPO_FERIADO_LABELS: Record<TipoFeriado, string> = {
  [TipoFeriado.Nacional]: 'Nacional',
  [TipoFeriado.Estadual]: 'Estadual',
  [TipoFeriado.Municipal]: 'Municipal',
  [TipoFeriado.Institucional]: 'Institucional',
}

/* -------------------------------------------------------------------------- */
/* Formatação                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Formata um `LocalTime` do backend (`HH:mm` ou `HH:mm:ss`, sem fuso) para
 * `HH:mm`. Não usa `Date` de propósito: `new Date('09:30')` é inválido e
 * `new Date('2026-01-01T09:30')` reintroduziria conversão de fuso.
 */
export const formatHorario = (horario?: string | null): string =>
  horario ? horario.slice(0, 5) : ''

/** `09:00 às 12:00`, ou `Dia inteiro` quando não há faixa de horário. */
export const formatFaixaHorario = (inicio?: string | null, termino?: string | null): string => {
  const de = formatHorario(inicio)
  const ate = formatHorario(termino)
  if (!de && !ate) return 'Dia inteiro'
  if (de && ate) return `${de} às ${ate}`
  return de || ate
}

export const formatDiasSemana = (dias?: DiaSemana[] | null): string =>
  dias?.length ? dias.map((dia) => DIAS_SEMANA_LABELS[dia] ?? dia).join(', ') : 'Todos os dias'

/* -------------------------------------------------------------------------- */
/* Estados do calendário                                                      */
/* -------------------------------------------------------------------------- */

export type EstadoAgenda = 'disponivel' | 'indisponivel' | 'indisponibilidade-global'

/**
 * Rótulo textual de cada estado. **A cor nunca é o único portador do
 * significado** (BRAND.md + WCAG 2.2 AA): todo bloco do calendário mostra este
 * texto e um ícone além do tom de fundo.
 */
export const ESTADO_AGENDA_LABELS: Record<EstadoAgenda, string> = {
  disponivel: 'Disponível',
  indisponivel: 'Indisponível',
  'indisponibilidade-global': 'Indisponibilidade do profissional',
}

/** Ícone (Bootstrap Icons) de cada estado — par obrigatório da cor. */
export const ESTADO_AGENDA_ICONES: Record<EstadoAgenda, string> = {
  disponivel: 'bi-check-circle',
  indisponivel: 'bi-slash-circle',
  'indisponibilidade-global': 'bi-globe2',
}

/**
 * Um bloco de agenda que a empresa **vê mas não gerencia**: é a
 * indisponibilidade global do profissional, registrada sem empresa nenhuma e
 * portanto válida para todas em que ele é médico ativo.
 *
 * O sinal está no próprio payload: `EscalaAgendaViewEmpresa.getVinculos()` vem
 * **vazio** exatamente nesse caso (`findByEmpresa` não muda, e o registro global
 * não tem `EscalaAgendaEmpresa`). Os endpoints de detalhe
 * (`GET /escalas/empresa/{empresa}/{id}` e `GET /escalas/empresa/agenda/{empresa}/{id}`)
 * passam pelas guardas `valida*Empresa` e devolvem **403** para esses registros —
 * por isso a tela não oferece abrir, editar nem excluir, em vez de deixar o
 * usuário descobrir isso com um erro de permissão.
 */
export const naoGerenciavelPelaEmpresa = (agenda: EscalaAgendaViewEmpresa): boolean =>
  agenda.indisponivel && !agenda.vinculos?.length

export const estadoDaAgendaPessoal = (agenda: EscalaAgendaViewPessoa): EstadoAgenda => {
  if (!agenda.indisponivel) return 'disponivel'
  return agenda.vinculos?.length ? 'indisponivel' : 'indisponibilidade-global'
}

export const estadoDaAgendaEmpresa = (agenda: EscalaAgendaViewEmpresa): EstadoAgenda => {
  if (naoGerenciavelPelaEmpresa(agenda)) return 'indisponibilidade-global'
  return agenda.indisponivel ? 'indisponivel' : 'disponivel'
}

/* -------------------------------------------------------------------------- */
/* Erros de validação (422)                                                   */
/* -------------------------------------------------------------------------- */

export type ErrosValidacao = {
  mensagem?: string
  /** Um item por descarte, por campo — ex.: `{ vinculos: ['Empresa 99: ...'] }`. */
  porCampo: Record<string, string[]>
  /** Todas as mensagens achatadas, na ordem em que vieram. */
  todas: string[]
}

const ERROS_VAZIOS: ErrosValidacao = { porCampo: {}, todas: [] }

/**
 * Lê o envelope de erro do backend (`GenericResponse` → `{ data: { message, errors } }`,
 * devolvido com 422 por `InvalidDataException`).
 *
 * É o que sustenta o AC "empresa ou especialidade descartada por falta de
 * vínculo é informada ao usuário, não silenciada": `processaVinculos()` acumula
 * os itens recusados e devolve um por linha em `errors.vinculos`. A tela lista
 * cada um junto ao campo — não pode virar toast genérico.
 */
export const extrairErrosValidacao = (error: unknown): ErrosValidacao => {
  const payload = (error as { response?: { data?: { data?: unknown } } })?.response?.data?.data

  if (!payload || typeof payload !== 'object') return ERROS_VAZIOS

  const { message, errors } = payload as { message?: string; errors?: unknown }

  const porCampo: Record<string, string[]> = {}
  if (errors && typeof errors === 'object') {
    Object.entries(errors as Record<string, unknown>).forEach(([campo, valor]) => {
      const lista = (Array.isArray(valor) ? valor : [valor]).filter(
        (item): item is string => typeof item === 'string' && item.length > 0
      )
      if (lista.length) porCampo[campo] = lista
    })
  }

  return {
    mensagem: typeof message === 'string' ? message : undefined,
    porCampo,
    todas: Object.values(porCampo).flat(),
  }
}

/** `true` quando o backend recusou por unicidade (`escalas_uk`/`escalas_agenda_uk`). */
export const ehErroDeEscalaDuplicada = (erros: ErrosValidacao): boolean =>
  /j[áa] cadastrad/i.test(erros.mensagem ?? '')

/**
 * Explicação em português do 422 de unicidade. `escalas_agenda_uk` é único em
 * `(id_pessoa, tipo_escala, data, horario_inicio)`: a mesma pessoa não pode ter,
 * no mesmo dia e horário, uma indisponibilidade global **e** outra escopada a
 * uma empresa — a segunda bate na restrição. É comportamento pré-existente do
 * banco; o que a tela deve a ele é dizer isso com clareza.
 */
export const MENSAGEM_ESCALA_DUPLICADA =
  'Já existe uma marcação sua desse mesmo tipo nesse dia e horário. ' +
  'Uma indisponibilidade registrada para todas as instituições e outra registrada para uma ' +
  'instituição específica não podem ocupar o mesmo dia e horário — exclua a existente antes ' +
  'de criar a nova, ou escolha outro horário.'

/**
 * Mensagem de último recurso quando o erro **não** traz o envelope do backend:
 * falha de rede, timeout, CORS ou HTML de proxy. Sem ela o usuário clica em
 * salvar e a tela fica exatamente igual — erro engolido, sem feedback.
 */
export const MENSAGEM_FALHA_GENERICA =
  'Não foi possível concluir a operação. Verifique sua conexão e tente novamente.'

/**
 * Itens do bloco "Não foi possível salvar", já sem os campos que a tela mostra
 * em blocos próprios (`vinculos` e o aviso de duplicidade).
 *
 * **Sempre devolve ao menos uma mensagem quando houve erro e nada mais foi
 * exibido** — é o que garante feedback no caso sem envelope.
 */
export const mensagensDeFalha = (
  erros: ErrosValidacao | null | undefined,
  opcoes: { exibiuVinculos?: boolean; exibiuDuplicada?: boolean } = {}
): string[] => {
  if (!erros) return []

  const outros = Object.entries(erros.porCampo)
    .filter(([campo]) => campo !== 'vinculos')
    .flatMap(([campo, mensagens]) => mensagens.map((mensagem) => `${campo}: ${mensagem}`))

  if (outros.length) return outros
  if (opcoes.exibiuVinculos || opcoes.exibiuDuplicada) return []

  return [erros.mensagem ?? MENSAGEM_FALHA_GENERICA]
}

/* -------------------------------------------------------------------------- */
/* Permissão de feriado                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Espelha o `permission.json` do backend — perfis `Feriado:*`, concedidos por
 * **grupo** (o mesmo `currentUser.grupo.nome` que o `PrivateRoutes` usa):
 *
 * ```
 * Feriado:read                      SUPER_ADMIN, ADMIN, MEDICO, EMPLOYEE
 * Feriado:create|update|delete      SUPER_ADMIN, ADMIN
 * ```
 *
 * `SUPER_MEDICO` **não tem nem leitura** — por isso a aba inteira some para ele,
 * em vez de abrir e o `GET /feriados` voltar 403 com "Access Denied" em inglês.
 * A regra é a mesma que `podeVerEixoEmpresa` aplica em `EscalasPage`: nada de
 * botão que devolve 403 ao ser clicado.
 */
const GRUPOS_FERIADO_LEITURA: string[] = [
  Permission.SuperAdmin,
  Permission.Admin,
  Permission.Medico,
  Permission.Employee,
]

const GRUPOS_FERIADO_ESCRITA: string[] = [Permission.SuperAdmin, Permission.Admin]

export const podeLerFeriado = (grupo?: string | null): boolean =>
  !!grupo && GRUPOS_FERIADO_LEITURA.includes(grupo)

export const podeEscreverFeriado = (grupo?: string | null): boolean =>
  !!grupo && GRUPOS_FERIADO_ESCRITA.includes(grupo)

/* -------------------------------------------------------------------------- */
/* Acesso à área de escala                                                    */
/* -------------------------------------------------------------------------- */

/** Recorte do usuário logado que a regra lê — basta o `currentUser` do `useAuth`. */
export type UsuarioAcessoEscala = {
  grupo?: { nome?: string } | null
  vinculos?: Array<{
    ativo?: boolean
    cargo?: string
    medico?: boolean
    responsavel?: boolean
  }> | null
}

export type AcessoEscala = { eixoPessoal: boolean; eixoEmpresa: boolean }

/**
 * Quem vê cada eixo da escala. **Fonte única** para o menu lateral e para as
 * abas da página, que antes usavam critérios diferentes (um item de menu que
 * levava a "sem acesso", e o inverso).
 *
 * Eixo empresa, nesta primeira versão:
 * - SUPER_ADMIN: todas as empresas;
 * - SUPER_MEDICO: as empresas em que tem permissão;
 * - ADMIN ou responsável/administrador da empresa no vínculo: a própria empresa;
 * - colaborador (inclusive com `gestorAgenda`): **não vê**.
 *
 * Quais empresas cada um enxerga é decisão do backend: o seletor usa
 * `GET /empresas/admin` (`addAutorizacoesAdmin`) e toda rota do eixo passa por
 * `hasPermission(#empresa, 'Empresa', 'Escala:*')`. Aqui só se decide se a aba
 * aparece.
 */
export const acessoEscala = (usuario?: UsuarioAcessoEscala | null): AcessoEscala => {
  const grupo = usuario?.grupo?.nome
  const vinculos = usuario?.vinculos?.filter((vinculo) => vinculo.ativo) ?? []

  const superAdmin = grupo === Permission.SuperAdmin
  const superMedico = grupo === Permission.SuperMedico
  const admin = grupo === Permission.Admin
  const medico = vinculos.some((vinculo) => vinculo.cargo === CargoType.Medico || vinculo.medico)
  const responsavelDaEmpresa = vinculos.some(
    (vinculo) =>
      vinculo.cargo === CargoType.Responsavel ||
      vinculo.cargo === CargoType.Administrador ||
      vinculo.responsavel
  )

  return {
    // O eixo pessoal é escopado à pessoa logada pelo backend
    // (`valida*PessoaLogada` + injeção de `pessoa:<id>` no search).
    eixoPessoal: superAdmin || superMedico || medico,
    eixoEmpresa: superAdmin || superMedico || admin || responsavelDaEmpresa,
  }
}
